import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, SpeakerHigh, X, Sparkle, Flag, House } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api, speakWord } from "./api";

const DIFF_VARIANT = { easy: "success", medium: "warning", hard: "primary" };

/* ── Tappable text ──────────────────────────────────────── */
function TappableText({ text, onTap }) {
  return (
    <span>
      {text.split(/(\s+)/).map((part, i) => {
        const clean = part.replace(/[^a-zA-Z'-]/g, "");
        if (!clean || clean.length < 3 || /\s/.test(part)) return <span key={i}>{part}</span>;
        return (
          <button key={i} onClick={() => onTap(clean)}
            className="rounded underline decoration-border decoration-dotted underline-offset-4 transition-colors hover:bg-accent hover:decoration-primary">
            {part}
          </button>
        );
      })}
    </span>
  );
}

/* ── Lookup popup ───────────────────────────────────────── */
function LookupPopup({ lookup, onClose }) {
  return (
    <AnimatePresence>
      {lookup && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center px-5"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
          <motion.div data-testid="lookup-popup"
            initial={{ scale: 0.9, opacity: 0, y: 16 }} animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className="relative w-full max-w-sm rounded-[2rem] bg-card p-6 text-center shadow-soft-lg">
            <button onClick={onClose} data-testid="lookup-close" aria-label="Close"
              className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted transition hover:bg-border">
              <X size={18} weight="bold" />
            </button>
            {lookup.loading ? (
              <div className="py-6">
                <Mascot size={64} mood="happy" animated={false} className="mx-auto" />
                <p className="mt-2 text-sm font-bold text-muted-foreground">Looking it up…</p>
              </div>
            ) : lookup.found ? (
              <>
                <div className="flex items-center justify-center gap-2">
                  <h3 className="font-display text-2xl font-black capitalize text-foreground" data-testid="lookup-word">{lookup.word}</h3>
                  <button onClick={() => speakWord(lookup.word)} aria-label="Say the word" data-testid="lookup-speak"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-primary-strong">
                    <SpeakerHigh size={18} weight="fill" />
                  </button>
                </div>
                {lookup.word_type && <BloomBadge variant="neutral" className="mt-1 px-2.5 py-0.5 text-xs">{lookup.word_type}</BloomBadge>}
                <p className="mt-3 text-base font-semibold leading-relaxed text-foreground/85" data-testid="lookup-meaning">"{lookup.meaning}"</p>
              </>
            ) : (
              <>
                <Mascot size={64} mood="encouraging" animated={false} className="mx-auto" />
                <h3 className="mt-2 font-display text-xl font-black capitalize text-foreground">{lookup.word}</h3>
                <p className="mt-2 text-sm font-semibold text-muted-foreground" data-testid="lookup-missing-msg">
                  We're still growing this word! We've made a note to add it. 🌱
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── Inline single image (portrait, tappable) ───────────── */
function SingleWordImage({ url, alt, label, labelBg, caption, onClick }) {
  const [errored, setErrored] = useState(false);
  if (!url || errored) return null;

  return (
    <div
      onClick={onClick}
      className={cn(
        "overflow-hidden rounded-2xl border border-border/40 shadow-soft",
        onClick && "cursor-pointer transition-transform active:scale-[0.98] hover:brightness-95",
      )}
    >
      {label && (
        <div className={cn("px-3 py-1.5 text-center", labelBg)}>
          <span className="text-[11px] font-extrabold uppercase tracking-wider text-white">{label}</span>
        </div>
      )}
      <img
        src={url}
        alt={alt || ""}
        onError={() => setErrored(true)}
        className="w-full object-cover"
        style={{ aspectRatio: "3/4" }}
        draggable={false}
      />
      {caption && (
        <p className="border-t border-border/40 bg-muted/50 px-3 py-2 text-center text-xs font-semibold italic text-foreground/70">
          {caption}
        </p>
      )}
    </div>
  );
}

/* ── Inline image panel (VS side-by-side) ──────────────── */
function WordImagePanel({ word, onImageClick }) {
  const hasWord    = !!word.image_url;
  const hasAntonym = !!word.antonym_image_url;
  if (!hasWord && !hasAntonym) return null;


  /* Single image (only one exists) — constrained width so it doesn't fill full card */
  if (!hasWord || !hasAntonym) {
    const url     = hasWord ? word.image_url           : word.antonym_image_url;
    const alt     = hasWord ? word.word                : word.antonyms?.[0];
    const label   = hasWord ? word.image_label         : word.antonym_image_label;
    const caption = hasWord ? word.image_caption       : word.antonym_image_caption;
    const bg      = hasWord ? "bg-teal"                : "bg-destructive";
    return (
      <div className="mx-auto w-full max-w-[260px] sm:max-w-[300px]">
        <SingleWordImage url={url} alt={alt} label={label}
          labelBg={bg} caption={caption} onClick={onImageClick} />
      </div>
    );
  }

  /* VS side-by-side — label bar + image + bottom words banner */
  // Bottom text: explicit caption → or auto-pull synonyms / antonyms
  const wordBottom    = word.image_caption
    || (word.synonyms?.length  > 0 ? word.synonyms.slice(0, 3).join("  ·  ")  : null);
  const antonymBottom = word.antonym_image_caption
    || (word.antonyms?.length  > 0 ? word.antonyms.slice(0, 3).join("  ·  ")  : null);

  return (
    <div className="relative overflow-hidden rounded-2xl shadow-soft">
      <div className="flex items-stretch">
        {/* Word card */}
        <div
          onClick={onImageClick}
          className="flex flex-1 cursor-pointer flex-col overflow-hidden transition-transform active:scale-[0.98] hover:brightness-95"
        >
          {/* Top label — teal, always same height for alignment */}
          <div className="flex min-h-[30px] items-center justify-center bg-teal px-2 py-1">
            {word.image_label && (
              <span className="text-[10px] font-extrabold uppercase tracking-wide text-white">
                {word.image_label}
              </span>
            )}
          </div>
          {/* Image */}
          <img src={word.image_url} alt={word.word} draggable={false}
            className="block w-full flex-1 object-cover"
            style={{ aspectRatio: "3/4" }} />
          {/* Bottom words — synonyms */}
          <div className="flex min-h-[28px] items-center justify-center bg-teal/90 px-2 py-1.5">
            {wordBottom && (
              <span className="text-center text-[9px] font-semibold leading-tight text-white">
                {wordBottom}
              </span>
            )}
          </div>
        </div>

        {/* Antonym card */}
        <div
          onClick={onImageClick}
          className="flex flex-1 cursor-pointer flex-col overflow-hidden transition-transform active:scale-[0.98] hover:brightness-95"
        >
          {/* Top label — red, always same height for alignment */}
          <div className="flex min-h-[30px] items-center justify-center bg-destructive px-2 py-1">
            {word.antonym_image_label && (
              <span className="text-[10px] font-extrabold uppercase tracking-wide text-white">
                {word.antonym_image_label}
              </span>
            )}
          </div>
          {/* Image */}
          <img src={word.antonym_image_url} alt={word.antonyms?.[0] || "opposite"} draggable={false}
            className="block w-full flex-1 object-cover"
            style={{ aspectRatio: "3/4" }} />
          {/* Bottom words — antonyms */}
          <div className="flex min-h-[28px] items-center justify-center bg-destructive/90 px-2 py-1.5">
            {antonymBottom && (
              <span className="text-center text-[9px] font-semibold leading-tight text-white">
                {antonymBottom}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* VS badge — centred in image area (offset by label+bottom bars) */}
      <div className="absolute left-1/2 top-[calc(50%+1px)] z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-white font-display text-xs font-black text-foreground shadow-lg">
          VS
        </span>
      </div>
    </div>
  );
}

/* ── Image lightbox ─────────────────────────────────────── */
function ImageLightbox({ word, onClose }) {
  const [slide,     setSlide]     = useState(0);
  const [direction, setDirection] = useState(1);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const images = [
    word.image_url ? {
      url:     word.image_url,
      alt:     word.word,
      label:   word.image_label,
      caption: word.image_caption
               || (word.synonyms?.length > 0 ? word.synonyms.slice(0, 3).join("  ·  ") : null),
      labelBg: "bg-teal",
    } : null,
    word.antonym_image_url ? {
      url:     word.antonym_image_url,
      alt:     word.antonyms?.[0],
      label:   word.antonym_image_label,
      caption: word.antonym_image_caption
               || (word.antonyms?.length > 0 ? word.antonyms.slice(0, 3).join("  ·  ") : null),
      labelBg: "bg-destructive",
    } : null,
  ].filter(Boolean);

  const hasMultiple = images.length > 1;

  const goTo = (i) => {
    if (i === slide) return;
    setDirection(i > slide ? 1 : -1);
    setSlide(i);
  };
  const goNext = () => { if (slide < images.length - 1) goTo(slide + 1); };
  const goPrev = () => { if (slide > 0) goTo(slide - 1); };

  /* Touch swipe */
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = touchStartX.current - e.changedTouches[0].clientX;
    const dy = touchStartY.current - e.changedTouches[0].clientY;
    touchStartX.current = null;
    touchStartY.current = null;
    // Swipe down → close
    if (Math.abs(dy) > Math.abs(dx) && dy < -70) { onClose(); return; }
    // Horizontal swipe → next / prev
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      dx > 0 ? goNext() : goPrev();
    }
  };

  /* Keyboard navigation */
  useEffect(() => {
    const fn = (e) => {
      if (e.key === "Escape")     onClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft")  goPrev();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  });

  const current = images[slide];

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/88 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* ✕ Close */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
      >
        <X size={20} weight="bold" />
      </button>

      {/* Card image — spring in, swipeable */}
      <div
        className="relative flex w-full flex-1 items-center justify-center px-6 sm:px-12"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Left arrow (desktop) */}
        {slide > 0 && (
          <button onClick={goPrev} aria-label="Previous"
            className="absolute left-2 z-10 hidden h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 sm:flex">
            <ArrowLeft size={20} weight="bold" />
          </button>
        )}

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={slide}
            custom={direction}
            variants={{
              enter:  (d) => ({ x: d > 0 ? 280 : -280, opacity: 0 }),
              center: { x: 0, opacity: 1 },
              exit:   (d) => ({ x: d > 0 ? -280 : 280, opacity: 0 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="flex w-full justify-center"
          >
            <div
              className="flex flex-col"
              style={{ maxWidth: "clamp(280px, 50vw, 420px)" }}
            >
              {current.label && (
                <div className={cn("flex items-center justify-center rounded-t-2xl py-2 px-3", current.labelBg)}>
                  <span className="text-sm font-extrabold uppercase tracking-wide text-white">
                    {current.label}
                  </span>
                </div>
              )}
              <img
                src={current.url}
                alt={current.alt || ""}
                draggable={false}
                className={cn(
                  "w-full object-contain shadow-2xl",
                  current.label ? "" : "rounded-t-3xl",
                  current.caption ? "" : "rounded-b-3xl",
                )}
                style={{ maxHeight: "68svh" }}
              />
              {current.caption && (
                <div className={cn("flex items-center justify-center rounded-b-2xl py-2 px-3", current.labelBg)}>
                  <span className="text-xs font-semibold text-white">{current.caption}</span>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Right arrow (desktop) */}
        {slide < images.length - 1 && (
          <button onClick={goNext} aria-label="Next"
            className="absolute right-2 z-10 hidden h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 sm:flex">
            <ArrowRight size={20} weight="bold" />
          </button>
        )}
      </div>

      {/* Bottom indicators + hint */}
      <div className="mb-8 mt-4 flex flex-col items-center gap-2">
        {hasMultiple && (
          <>
            {/* Dot indicators */}
            <div className="flex gap-2 rounded-full bg-black/50 px-4 py-2.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); goTo(i); }}
                  aria-label={`Card ${i + 1}`}
                  className={cn(
                    "h-2 rounded-full transition-all duration-200",
                    i === slide ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/70",
                  )}
                />
              ))}
            </div>

            {/* Swipe hint */}
            <AnimatePresence mode="wait">
              <motion.p
                key={slide}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-xs font-bold text-white/90"
              >
                {slide === 0 ? "swipe to see the opposite →" : "← swipe back to word"}
              </motion.p>
            </AnimatePresence>
          </>
        )}
        <p className="text-[11px] text-white/30">tap outside or swipe down to close</p>
      </div>
    </motion.div>
  );
}


/* ── Child signal modal ─────────────────────────────────── */
const SIGNAL_REASONS = [
  "Something looks wrong",
  "I don't understand this",
  "I don't like the picture",
];

function ChildSignalModal({ word, childId, onClose }) {
  const [selected, setSelected] = useState(null);
  const [sent,     setSent]     = useState(false);
  const [saving,   setSaving]   = useState(false);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api("/notifications/signal", {
        method: "POST",
        body: { child_id: childId, word_id: word.word_id, reason: selected },
      });
      setSent(true);
    } catch { /* show thanks anyway */ setSent(true); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button aria-label="Close" onClick={onClose}
        className="absolute inset-0 bg-foreground/25 backdrop-blur-[2px]" />
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        className="relative w-full max-w-sm rounded-t-[2rem] bg-card p-6 shadow-soft-lg sm:rounded-[2rem]"
      >
        <button onClick={onClose}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border">
          <X size={16} weight="bold" />
        </button>

        {sent ? (
          <div className="py-4 text-center">
            <p className="text-2xl">👍</p>
            <p className="mt-2 font-display text-lg font-black text-foreground">Got it!</p>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              We've let your grown-up know. They'll take a look!
            </p>
            <button onClick={onClose}
              className="mt-4 rounded-full bg-primary px-6 py-2.5 font-display text-sm font-black text-primary-foreground shadow-glow">
              Back to learning
            </button>
          </div>
        ) : (
          <>
            <p className="font-display text-lg font-black text-foreground">
              Not sure about this word? 🤔
            </p>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              Tell your grown-up what you think — pick one:
            </p>
            <div className="mt-4 space-y-2">
              {SIGNAL_REASONS.map((r) => (
                <button key={r} onClick={() => setSelected(r)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left text-sm font-bold transition",
                    selected === r
                      ? "border-primary bg-accent text-primary-strong"
                      : "border-border text-foreground hover:border-primary/40",
                  )}>
                  <span className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                    selected === r ? "border-primary bg-primary" : "border-border",
                  )}>
                    {selected === r && <span className="h-2 w-2 rounded-full bg-white" />}
                  </span>
                  {r}
                </button>
              ))}
            </div>
            <button
              onClick={submit}
              disabled={!selected || saving}
              className="mt-4 w-full rounded-full bg-primary py-3 font-display text-sm font-black text-primary-foreground shadow-glow transition disabled:opacity-40"
            >
              {saving ? "Sending…" : "Tell my grown-up"}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

/* ── Main LearnPage ─────────────────────────────────────── */
export default function LearnPage() {
  const { childId }     = useParams();
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const allWordsParam   = searchParams.get("all_words") === "true";
  const planId          = searchParams.get("plan_id") || null;
  const [words,    setWords]    = useState(null);
  const [idx,      setIdx]      = useState(0);
  const [lookup,   setLookup]   = useState(null);
  const [finished, setFinished] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [signal,   setSignal]   = useState(false); // child signal modal
  const viewed = useRef(new Set());

  useEffect(() => {
    api(`/learn/words?child_id=${childId}&limit=10${(allWordsParam || planId) ? '&all_words=true' : ''}${planId ? `&plan_id=${planId}` : ''}`)
      .then(setWords)
      .catch(() => navigate(`/vocab/child/${childId}`, { replace: true }));
  }, [childId, navigate]);

  const word      = words?.[idx];
  const hasImages = !!(word?.image_url || word?.antonym_image_url);

  useEffect(() => {
    if (!word || viewed.current.has(word.word_id)) return;
    viewed.current.add(word.word_id);
    api("/learn/view", { method: "POST", body: { child_id: childId, word_id: word.word_id } }).catch(() => {});
  }, [word, childId]);

  const doLookup = useCallback(async (w, sourceType) => {
    if (!word || w.toLowerCase() === word.word.toLowerCase()) return;
    setLookup({ loading: true, word: w });
    try {
      const res = await api(`/lookup?w=${encodeURIComponent(w)}&child_id=${childId}&source_word_id=${word.word_id}&source_type=${sourceType}`);
      setLookup({ loading: false, ...res });
    } catch {
      setLookup(null);
    }
  }, [word, childId]);

  /* ── Finished screen ─────────────────────────────────── */
  if (finished) {
    return (
      <div className="flex min-h-[100svh] flex-col items-center justify-center bg-gradient-to-b from-[#FFF7EF] to-[#DCF3EC] px-6 text-center" data-testid="learn-finished">
        <Mascot size={110} mood="celebrating" />
        <h1 className="mt-4 font-display text-3xl font-black text-foreground">Brilliant learning!</h1>
        <p className="mt-2 text-base font-semibold text-muted-foreground">
          You explored {words.length} words today. They're growing in your Bloom!
        </p>
        <div className="mt-6">
          <BloomButton onClick={() => navigate(planId ? `/vocab/child/${childId}/plans` : `/vocab/child/${childId}`)} data-testid="back-home-btn">
            All done!
          </BloomButton>
        </div>
      </div>
    );
  }

  /* ── Learn screen ────────────────────────────────────── */
  return (
    <div className="min-h-[100svh] bg-gradient-to-b from-[#FFF7EF] via-[#FFF3E8] to-[#DCF3EC] pb-24" data-testid="learn-page">

      {/* Top nav */}
      <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3 sm:px-0 sm:pt-6">
        <button data-testid="learn-home-btn" aria-label="Home"
          onClick={() => navigate(planId ? `/vocab/child/${childId}/plans` : `/vocab/child/${childId}`)}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-card/85 text-foreground shadow-soft backdrop-blur">
          <House size={20} weight="bold" />
        </button>
        <span className="rounded-full bg-card/85 px-4 py-2 font-display text-sm font-black text-foreground shadow-soft backdrop-blur" data-testid="learn-progress">
          {words ? `${idx + 1} / ${words.length}` : "…"}
        </span>
        <span className="w-11" />
      </div>

      {/* Word card */}
      <div className="mx-auto max-w-2xl px-4 sm:px-0">
        {!word && <div className="mt-4 h-96 animate-pulse rounded-[2rem] bg-muted" />}

        {word && (
          <AnimatePresence mode="wait">
            <motion.div
              key={word.word_id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              className="mt-2 rounded-[2rem] border border-border bg-card p-6 shadow-soft-lg sm:p-8"
              data-testid="learn-card"
            >
              <div>

                {/* ── Main content ─────────────────────────── */}
                <div>
                  {/* Word + speaker + badges */}
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="font-display text-4xl font-black capitalize tracking-tight text-foreground sm:text-5xl" data-testid="learn-word">
                      {word.word}
                    </h1>
                    <button onClick={() => speakWord(word.word)} aria-label="Say the word" data-testid="learn-speak-btn"
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow transition active:scale-90">
                      <SpeakerHigh size={22} weight="fill" />
                    </button>
                    <span className="flex gap-1.5">
                      {word.word_type  && <BloomBadge variant="neutral"                        className="px-2.5 py-1 text-xs">{word.word_type}</BloomBadge>}
                      {word.difficulty && <BloomBadge variant={DIFF_VARIANT[word.difficulty] || "neutral"} className="px-2.5 py-1 text-xs">{word.difficulty}</BloomBadge>}
                    </span>
                  </div>

                  {/* Meaning */}
                  <p className="mt-4 text-lg font-semibold leading-relaxed text-foreground/90" data-testid="learn-meaning">
                    <TappableText text={word.meaning || ""} onTap={(w) => doLookup(w, "meaning")} />
                  </p>

                  {/* Synonyms */}
                  {word.synonyms?.length > 0 && (
                    <div className="mt-5">
                      <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Synonyms</p>
                      <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">Words with a similar meaning — tap any to peek!</p>
                      <div className="flex flex-wrap gap-2">
                        {word.synonyms.map((s) => (
                          <button key={s} data-testid={`synonym-${s.replace(/\s+/g, "-")}`} onClick={() => doLookup(s, "synonym")}
                            className="rounded-full bg-teal/15 px-3.5 py-2 text-sm font-extrabold text-teal-ink transition hover:brightness-95 active:scale-95">
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Antonyms */}
                  {word.antonyms?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">Antonyms</p>
                      <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">Words with the opposite meaning</p>
                      <div className="flex flex-wrap gap-2">
                        {word.antonyms.map((a) => (
                          <button key={a} data-testid={`antonym-${a.replace(/\s+/g, "-")}`} onClick={() => doLookup(a, "antonym")}
                            className="rounded-full bg-destructive/10 px-3.5 py-2 text-sm font-extrabold text-destructive transition hover:brightness-95 active:scale-95">
                            {a}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mobile image panel */}
                  {hasImages && (
                    <div className="mt-5">
                      <WordImagePanel word={word} variant="vs" onImageClick={() => setLightbox(true)} />
                    </div>
                  )}

                  {/* Child signal button */}
          <div className="mt-5 flex justify-center">
            <button
              onClick={() => setSignal(true)}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold text-muted-foreground/60 hover:bg-muted/60 hover:text-muted-foreground transition"
              data-testid="signal-btn"
            >
              <Flag size={11} weight="bold" />
              Not sure about this word?
            </button>
          </div>

          {/* Sentences */}
                  {word.sentences?.length > 0 && (
                    <div className="mt-5 space-y-2">
                      <p className="text-[11px] font-extrabold uppercase tracking-wider text-muted-foreground">In a sentence</p>
                      <p className="mb-2 text-[10px] font-semibold text-muted-foreground/70">See how the word is used</p>
                      {word.sentences.map((s, i) => (
                        <p key={i} className="rounded-2xl bg-muted/60 px-4 py-3 text-base font-semibold text-foreground/85">
                          <TappableText text={s} onTap={(w) => doLookup(w, "sentence")} />
                        </p>
                      ))}
                    </div>
                  )}
                </div>


              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Sticky bottom navigation — always visible, no scrolling needed */}
      {words && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 px-4 py-3 backdrop-blur-sm">
          <div className="mx-auto flex max-w-2xl items-center justify-between">
            <BloomButton variant="outline" size="sm" leftIcon={ArrowLeft}
              disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} data-testid="prev-word-btn">
              Back
            </BloomButton>
            {/* Dots for short lists, progress bar for long (target practice) */}
            {words.length <= 12 ? (
              <span className="flex gap-1.5 overflow-hidden max-w-[120px] sm:max-w-none flex-wrap justify-center">
                {words.map((_, i) => (
                  <span key={i} className={`h-2 shrink-0 rounded-full transition-all ${i === idx ? "w-4 bg-primary" : i < idx ? "w-2 bg-teal" : "w-2 bg-border"}`} />
                ))}
              </span>
            ) : (
              <div className="flex flex-col items-center gap-1 min-w-0 flex-1 px-3">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-border/60">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${((idx + 1) / words.length) * 100}%` }}
                  />
                </div>
                <span className="text-[10px] font-bold text-muted-foreground">
                  {idx + 1} of {words.length}
                </span>
              </div>
            )}
            {idx < words.length - 1 ? (
              <BloomButton size="sm" rightIcon={ArrowRight}
                onClick={() => setIdx((i) => i + 1)} data-testid="next-word-btn">
                Next word
              </BloomButton>
            ) : (
              <BloomButton size="sm" rightIcon={Sparkle}
                onClick={() => setFinished(true)} data-testid="finish-learn-btn">
                Finish
              </BloomButton>
            )}
          </div>
        </div>
      )}

      {/* Child signal modal */}
      <AnimatePresence>
        {signal && word && (
          <ChildSignalModal word={word} childId={childId} onClose={() => setSignal(false)} />
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && word && (
          <ImageLightbox word={word} onClose={() => setLightbox(false)} />
        )}
      </AnimatePresence>

      <LookupPopup lookup={lookup} onClose={() => setLookup(null)} />
    </div>
  );
}